import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useJourney } from '../../context/JourneyContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { ArrowLeft, BookOpen, ShoppingBag, Handshake, Users, Sparkles, ArrowRight, CheckCircle, MessageCircle, Calendar, ThumbsUp } from 'lucide-react';

const SUGGESTIONS = [
  {
    id: 'teacher-pl',
    title: 'Add Teacher PL',
    description: 'Equip your teachers to continue the movement journey with their own confidence-building.',
    icon: Users,
    cta: 'Explore Teacher PL',
    path: '/choose-path',
    color: 'bg-accent/20 text-accent'
  },
  {
    id: 'ls-plus',
    title: 'Unlock LS+ Resources',
    description: 'Access our library of videos, activities, and reflections to extend the impact.',
    icon: BookOpen,
    cta: 'Try LS+ free',
    path: '/ls-plus',
    color: 'bg-primary/20 text-primary'
  },
  {
    id: 'merch',
    title: 'Explore Merch',
    description: 'Keep the movement alive with Let\'s Siva gear for your school or team.',
    icon: ShoppingBag,
    cta: 'View merch',
    path: '/merch',
    color: 'bg-ochre-400/20 text-ochre-400'
  },
  {
    id: 'partnership',
    title: 'Partnership Opportunities',
    description: 'Want to do more? Explore ways to partner with Let\'s Siva for greater impact.',
    icon: Handshake,
    cta: 'Learn about partnerships',
    path: '/choose-path',
    color: 'bg-sage-400/20 text-sage-400'
  }
];

const NEXT_STEPS = [
  { icon: CheckCircle, text: 'We review your information' },
  { icon: MessageCircle, text: 'We contact you within 1-2 business days' },
  { icon: Calendar, text: 'We suggest next steps together' },
  { icon: ThumbsUp, text: 'You decide — no pressure' },
];

const ContinueJourney = () => {
  const navigate = useNavigate();
  const { journey, trackPageView, trackCtaClick, resetJourney } = useJourney();

  React.useEffect(() => {
    trackPageView('portal_continue_journey');
  }, [trackPageView]);

  const handleSuggestionClick = (suggestion) => {
    trackCtaClick(suggestion.id);
    navigate(suggestion.path);
  };

  const handleStartOver = () => {
    resetJourney();
    navigate('/');
  };

  return (
    <div 
      data-testid="continue-journey-screen"
      className="min-h-screen relative overflow-hidden bg-[#1C1917]"
    >
      {/* Background */}
      <div className="absolute top-20 left-0 w-96 h-96 bg-accent/5 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-0 w-80 h-80 bg-primary/5 rounded-full blur-3xl" />

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 py-12">
        {/* Navigation */}
        <Button
          data-testid="back-btn"
          variant="ghost"
          onClick={() => navigate('/portal/next-step')}
          className="mb-8 text-stone-400 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        {/* Success message */}
        <div className="text-center mb-10 animate-fade-in-up">
          <div className="w-20 h-20 rounded-full bg-accent/20 flex items-center justify-center mx-auto mb-6">
            <Sparkles className="w-10 h-10 text-accent" />
          </div>
          <h1 
            data-testid="continue-title"
            className="text-3xl sm:text-4xl font-heading font-bold text-white mb-4"
          >
            You're on your way!
          </h1>
          <p className="text-base sm:text-lg text-stone-400 max-w-xl mx-auto">
            We've received your enquiry and will be in touch soon.
          </p>
          <p className="text-sm text-stone-500 mt-3">
            If you need us urgently, email <a href="mailto:info@letssiva.com" className="text-primary hover:underline">info@letssiva.com</a>
          </p>
        </div>

        {/* What Happens Next - Clarity Section */}
        <Card className="bg-stone-900/80 border border-stone-800 rounded-3xl p-6 md:p-8 mb-10 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
          <h2 className="text-xl font-heading font-bold text-white mb-6 text-center">
            What happens next?
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {NEXT_STEPS.map((step, index) => {
              const Icon = step.icon;
              return (
                <div 
                  key={index}
                  className="flex items-center gap-3 p-3 rounded-xl bg-stone-800/50"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-stone-300">{step.text}</span>
                </div>
              );
            })}
          </div>
        </Card>

        {/* Suggestions */}
        <h3 className="text-lg font-heading font-bold text-white mb-4 text-center">
          Continue exploring
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {SUGGESTIONS.map((suggestion, index) => {
            const Icon = suggestion.icon;
            return (
              <Card
                key={suggestion.id}
                data-testid={`suggestion-${suggestion.id}`}
                onClick={() => handleSuggestionClick(suggestion)}
                className="bg-stone-900/50 border border-stone-800 p-6 cursor-pointer hover:border-primary/30 transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] rounded-2xl animate-fade-in-up opacity-0"
                style={{ animationDelay: `${0.2 + index * 0.1}s`, animationFillMode: 'forwards' }}
              >
                <div className={`w-12 h-12 rounded-2xl ${suggestion.color} flex items-center justify-center mb-4`}>
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-heading font-bold text-white mb-2">
                  {suggestion.title}
                </h3>
                <p className="text-stone-400 mb-4 text-sm">
                  {suggestion.description}
                </p>
                <Button variant="ghost" className="p-0 h-auto text-primary hover:text-primary/80">
                  {suggestion.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Journey summary */}
        {journey && (
          <div className="bg-stone-900/50 border border-stone-800 rounded-2xl p-6 mb-8 animate-fade-in-up" style={{ animationDelay: '0.6s' }}>
            <h3 className="text-sm font-medium text-stone-500 uppercase tracking-wider mb-4">
              Your Journey Summary
            </h3>
            <div className="flex flex-wrap gap-3">
              {journey.user_type && (
                <span className="px-4 py-2 rounded-full bg-primary/20 text-primary text-sm">
                  {journey.user_type}
                </span>
              )}
              {journey.outcome && (
                <span className="px-4 py-2 rounded-full bg-accent/20 text-accent text-sm">
                  {journey.outcome}
                </span>
              )}
              {journey.recommended_pathway && (
                <span className="px-4 py-2 rounded-full bg-ochre-400/20 text-ochre-400 text-sm">
                  {journey.recommended_pathway}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Start over */}
        <div className="text-center">
          <Button
            data-testid="start-over-btn"
            variant="ghost"
            onClick={handleStartOver}
            className="text-stone-500 hover:text-white"
          >
            Start a new journey
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ContinueJourney;
